import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';

import { BehaviorSubject, throwError, timeout, timer } from 'rxjs';
import { catchError, filter, retry, switchMap, take } from 'rxjs/operators';

import { AuthService } from '../services/auth.service';
import { ToastService } from '../services/toast.service';
import { SUPPRESS_ERROR_TOAST_STATUSES } from './http-context.tokens';

const REQUEST_TIMEOUT_MS = 30_000;
const RETRY_MAX_ATTEMPTS = 2;
const RETRY_BASE_DELAY_MS = 300;
const RETRYABLE_STATUSES = new Set([502, 503, 504]);
const PUBLIC_ROUTES = ['/assets/', '/home'];

let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

/**
 * Functional HTTP interceptor — Angular 20 pattern.
 * Attaches Bearer token, handles 401 refresh, portal-aware redirect, 30s timeout,
 * exponential-backoff retry for transient gateway errors on idempotent requests.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  if (PUBLIC_ROUTES.some(route => req.url.includes(route))) {
    return next(req);
  }

  // inject() must be called at the top level of the interceptor function
  const authService = inject(AuthService);
  const toastService = inject(ToastService);
  const router = inject(Router);

  const token = authService.getAccessToken();
  if (token) {
    req = req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
  }

  return next(req).pipe(
    timeout(REQUEST_TIMEOUT_MS),
    retry({
      count: RETRY_MAX_ATTEMPTS,
      delay: (error, retryAttempt) => {
        if (req.method === 'GET' && error instanceof HttpErrorResponse && RETRYABLE_STATUSES.has(error.status)) {
          return timer(RETRY_BASE_DELAY_MS * 2 ** retryAttempt);
        }
        return throwError(() => error);
      }
    }),
    catchError((error: HttpErrorResponse | Error) => {
      if (error.name === 'TimeoutError') {
        toastService.error('Request timed out. Please try again.');
        return throwError(() => error);
      }

      if (error instanceof HttpErrorResponse) {
        // A request may opt out of the error toast for specific statuses
        // (e.g. a background gate fetch that treats 404 as a non-event).
        const suppressed = req.context.get(SUPPRESS_ERROR_TOAST_STATUSES).includes(error.status);
        const showToast = (title: string, detail?: string) => {
          if (!suppressed) toastService.error(title, detail);
        };

        switch (error.status) {
          case 401:
            return handle401(req, next, authService, toastService, router);

          case 400:
          case 422: {
            // Validation errors — extract field messages or detail
            const { title, detail } = extractClientErrorMessage(error);
            showToast(title, detail);
            break;
          }

          case 403: {
            const msg = error.error?.detail || 'Access denied. You do not have permission to perform this action.';
            showToast('Forbidden', msg);
            break;
          }

          case 404: {
            const msg = error.error?.detail || 'The requested resource was not found.';
            showToast('Not Found', msg);
            break;
          }

          case 409: {
            const msg = error.error?.detail || 'A conflict occurred. Please refresh and try again.';
            showToast('Conflict', msg);
            break;
          }

          case 429:
            showToast('Too Many Requests', 'Too many requests. Please try again later.');
            break;

          case 502:
          case 503:
            showToast('Service Unavailable', 'Service temporarily unavailable. Please try again later.');
            break;

          case 504:
            showToast('Timeout', 'Request timed out. Please try again.');
            break;

          default:
            if (error.status >= 400 && error.status < 500) {
              const { title, detail } = extractClientErrorMessage(error);
              showToast(title, detail);
            } else if (error.status >= 500) {
              showToast('A server error occurred. Please try again later.');
            }
            break;
        }
      }

      return throwError(() => error);
    })
  );
};

function handle401(
  req: Parameters<HttpInterceptorFn>[0],
  next: Parameters<HttpInterceptorFn>[1],
  authService: AuthService,
  toastService: ToastService,
  router: Router
) {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);

    const refreshToken = authService.getRefreshToken();

    if (refreshToken && !refreshToken.startsWith('refresh-')) {
      return authService.refreshAccessToken(refreshToken).pipe(
        switchMap(response => {
          isRefreshing = false;
          authService.setAccessToken(response.accessToken);
          refreshTokenSubject.next(response.accessToken);
          return next(req.clone({ setHeaders: { Authorization: `Bearer ${response.accessToken}` } }));
        }),
        catchError(err => {
          isRefreshing = false;
          doRedirectToLogin(authService, toastService, router);
          return throwError(() => err);
        })
      );
    } else {
      isRefreshing = false;
      doRedirectToLogin(authService, toastService, router);
      return throwError(() => new Error('Session expired'));
    }
  } else {
    return refreshTokenSubject.pipe(
      filter((t): t is string => t !== null),
      take(1),
      switchMap(t => next(req.clone({ setHeaders: { Authorization: `Bearer ${t}` } })))
    );
  }
}

function doRedirectToLogin(authService: AuthService, toastService: ToastService, router: Router): void {
  authService.clearAuthData();
  toastService.error('Your session has expired. Please log in again.');
  router.navigate(['/login']);
}

/**
 * Extract a user-facing message from a client error (4xx) response.
 * The API is expected to return meaningful messages for client errors
 * (validation failures, not found, forbidden, etc.).
 */
function extractClientErrorMessage(error: HttpErrorResponse): { title: string; detail: string } {
  const body = error.error;

  // Validation errors array: [{ field, message }]
  if (Array.isArray(body?.errors) && body.errors.length > 0) {
    const messages = body.errors.map((e: { message?: string }) => e.message).filter(Boolean);
    if (messages.length > 0) {
      return { title: body.title || 'Validation Error', detail: messages.join(' ') };
    }
  }

  // RFC 7807 detail field
  if (typeof body?.detail === 'string' && body.detail.trim()) {
    return { title: body.title || 'Error', detail: body.detail };
  }

  // Standard message field
  if (typeof body?.message === 'string' && body.message.trim()) {
    return { title: body.title || 'Error', detail: body.message };
  }

  // At least a title from the API
  if (typeof body?.title === 'string' && body.title.trim()) {
    return { title: body.title, detail: '' };
  }

  // Plain string body
  if (typeof body === 'string' && body.trim()) {
    return { title: 'Error', detail: body };
  }

  // Nothing usable in the body — single generic fallback
  return { title: 'Request Failed', detail: `The server returned an error (${error.status}). Please try again.` };
}
